import { useMemo } from "react";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { ModalTitle } from "../../../enums/StringLiterals/modalTitle";
import { TableNames } from "../../../enums/tableNames";
import { NycbsExternalLogsModalData } from "../../../pages/nycbsExternal/types/nycbsExternalLogsModalData";
import CustomTable from "../../custom-table/custom-table";
import {
  generateNycbsExternalLogsTableData,
  nycbsExternalLogsTableHeaders,
} from "./nycbsExternalLogsModalTableConfig";

interface NycbsExternalLogsModalProps {
  onClose: () => void;
  orderData: NycbsExternalLogsModalData;
  /** True while the case's push history is still being read. */
  isLoading?: boolean;
}

/** Only the input batch is present until a push has been recorded. */
const INPUT_BATCH_ONLY_STAGE_COUNT = 1;

interface InfoFieldProps {
  label: string;
  value: string;
}

/**
 * Renders a labeled value in the Logs modal header grid.
 *
 * @param label - Field label shown above the value
 * @param value - Field value text
 * @returns Labeled field markup
 */
const InfoField = ({ label, value }: InfoFieldProps) => (
  <div className="flex min-w-0 flex-col gap-1.5">
    <span className="text-tiny font-medium text-primaryGray-8">{label}</span>
    <span className="truncate text-small font-semibold text-primaryGray-3">
      {value}
    </span>
  </div>
);

/**
 * Displays order batch stage history for NYCBS External PA Requests.
 *
 * @param onClose - Closes the modal
 * @param orderData - Populated view model for the selected order
 * @param isLoading - True while the case's push history is still being read
 * @returns Logs modal markup
 */
const NycbsExternalLogsModal = ({
  onClose,
  orderData,
  isLoading = false,
}: NycbsExternalLogsModalProps) => {
  const { pharmacyType } = orderData;

  const logsTableData = useMemo(
    () => generateNycbsExternalLogsTableData(orderData.stages),
    [orderData.stages],
  );

  const hasNoRecordedDelivery =
    !isLoading && orderData.stages.length <= INPUT_BATCH_ONLY_STAGE_COUNT;

  return (
    <Modal
      dialogId={ModalId.NYCBS_EXTERNAL_LOGS_MODAL}
      title={ModalTitle.NYCBS_EXTERNAL_LOGS_MODAL}
      onClose={onClose}
      onSave={onClose}
      saveButtonText="Cancel"
      hideFooter
      showSingleButton={true}
      heightPercentage={65}
      cancelText="Close"
    >
      <div className="nycbs-external-logs-modal flex h-full flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 rounded-lg p-4 md:grid-cols-3">
          <InfoField label="Seq" value={orderData.seq} />
          <InfoField label="MRN" value={orderData.mrn} />
          <InfoField label="Patient Name" value={orderData.patientName} />
          <InfoField label="Drug" value={orderData.drug} />
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-tiny font-medium text-primaryGray-8">
              Pharmacy Type
            </span>
            <span
              className="inline-flex w-fit rounded px-2 py-1 text-tiny font-semibold"
              style={{
                color: pharmacyType.color,
                backgroundColor: pharmacyType.bgColor,
              }}
            >
              {pharmacyType.text}
            </span>
          </div>
          <InfoField label="CMM Key" value={orderData.cmmKey} />
        </div>

        {isLoading ? (
          <div className="flex min-h-0 flex-1 items-center justify-center gap-4">
            <SpinningLoader />
            <p className="text-small text-primaryGray-8">
              Loading push history...
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <CustomTable
              tableHeaders={nycbsExternalLogsTableHeaders}
              tableData={logsTableData}
              tableName={TableNames.NYCBS_EXTERNAL_LOGS_STAGES_TABLE}
              itemsPerPage={10}
              pagesPerView={4}
              count={logsTableData.length}
              totalCount={logsTableData.length}
              hideSearchBar
              hidePagination
              disableHoverAndClick
            />
            {hasNoRecordedDelivery && (
              <p className="pt-4 text-small text-primaryGray-8">
                No delivery recorded for this case yet.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default NycbsExternalLogsModal;
